import { createContext, useCallback, useContext, useReducer } from 'react';

import type { Edge, Node } from '@xyflow/react';

import type { Recipe, TransformerVersion } from '../../../types';

// ========================================
// Types
// ========================================

export type EditorMode = 'visual' | 'yaml';
export type JobMode = 'runAsJob' | 'runAsEvent';

export type BottomTabType = 'runningStatus' | 'eventContext' | 'executionLog';

export interface BottomPanelTab {
  id: string;
  title: string;
  type: BottomTabType;
  closeable: boolean;
}

export interface RecipeMetadata {
  versionId: string;
  name: string;
  config: string;
  environ: string;
  description: string;
  languageVersion: string;
  processingType: string;
  maxDataProcTime: string;
  templateName: string;
}

export interface RecipeEditorState {
  // Recipe data
  recipe: Recipe | null;
  nodes: Node[];
  edges: Edge[];
  yamlContent: string;
  recipeMetadata: RecipeMetadata | null;

  // Editor state
  editorMode: EditorMode;
  isModified: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  isSwitchingMode: boolean; // Loading state during editor mode switch
  isSwitchingRecipe: boolean; // Loading state during recipe switch

  // History tracking
  canUndo: boolean;
  canRedo: boolean;

  // Toolbar state - File Operations
  autosaveEnabled: boolean;
  highlightSave: boolean; // Whether save button should be highlighted

  // Toolbar state - Execution
  jobMode: JobMode;
  isExecuting: boolean;
  currentExternalExecutionId: string | null; // Set when execution starts, used for log polling

  // Bottom panel tabs
  bottomPanelTabs: BottomPanelTab[];
  activeBottomTabId: string | null;

  // Toolbar state - Panels/Views
  showCompare: boolean;
  showMetadata: boolean;
  showMinimap: boolean;
  showDiagram: boolean; // Visual vs YAML (alias for editorMode)

  // Toolbar state - Editor utilities
  schemasFolded: boolean;
  selectedSqlQuery: string | null;
  showSchemaButton: boolean; // Whether to show schema fold button

  // Environment & Deployment
  selectedEnvironment: string | null;
  currentVersion: number;
  versions: TransformerVersion[];
  versionIsDeployed: boolean;
  isDeploying: boolean;
  isLatestVersion: boolean;

  // Template (tech-support only)
  showTemplateSelection: boolean;
  selectedTemplate: string;
  availableTemplates: string[];

  // Modals
  showVersionsDialog: boolean;
  showCheckInDialog: boolean;
  showDeployDialog: boolean;
  showScheduleDialog: boolean;
  showTemplateDialog: boolean;
  showMetadataDialog: boolean;
  showSqlEditorDialog: boolean;
  showSaveConflictDialog: boolean;
  showUnsavedChangesDialog: boolean;
  showCommandPalette: boolean;

  // Secondary toolbar
  showSecondaryToolbar: boolean;

  // Flow save state (for diagram mode)
  flowSaveState: {
    isSaving: boolean;
    hasUnsavedChanges: boolean;
    isComparingRecipes: boolean;
  };

  // Flow UI state
  error: boolean; // Loading error flag
  errorMessage: string | null; // Specific error message/type
  isSidebarCollapsed: boolean; // Sidebar collapsed state
  reactFlowKey: number; // For forcing ReactFlow re-renders
}

type RecipeEditorAction =
  // Recipe data actions
  | { type: 'SET_RECIPE'; payload: Recipe | null }
  | { type: 'SET_NODES'; payload: Node[] }
  | { type: 'SET_EDGES'; payload: Edge[] }
  | { type: 'SET_YAML_CONTENT'; payload: string }
  | { type: 'SET_RECIPE_METADATA'; payload: RecipeMetadata | null }
  // Editor state actions
  | { type: 'SET_EDITOR_MODE'; payload: EditorMode }
  | { type: 'SET_IS_MODIFIED'; payload: boolean }
  | { type: 'SET_IS_READ_ONLY'; payload: boolean }
  | { type: 'SET_IS_SAVING'; payload: boolean }
  | { type: 'SET_IS_SWITCHING_MODE'; payload: boolean }
  | { type: 'SET_IS_SWITCHING_RECIPE'; payload: boolean }
  // History actions
  | { type: 'SET_CAN_UNDO'; payload: boolean }
  | { type: 'SET_CAN_REDO'; payload: boolean }
  // File operations
  | { type: 'SET_AUTOSAVE_ENABLED'; payload: boolean }
  | { type: 'SET_HIGHLIGHT_SAVE'; payload: boolean }
  // Execution
  | { type: 'SET_JOB_MODE'; payload: JobMode }
  | { type: 'SET_IS_EXECUTING'; payload: boolean }
  | { type: 'SET_CURRENT_EXTERNAL_EXECUTION_ID'; payload: string | null }
  // Bottom panel tabs
  | { type: 'ADD_BOTTOM_TAB'; payload: BottomPanelTab }
  | { type: 'REMOVE_BOTTOM_TAB'; payload: string } // tab id
  | { type: 'SET_ACTIVE_BOTTOM_TAB'; payload: string | null }
  | { type: 'RENAME_BOTTOM_TAB'; payload: { id: string; title: string } }
  | { type: 'SET_BOTTOM_TAB_CLOSEABLE'; payload: { id: string; closeable: boolean } }
  // Panels/Views
  | { type: 'TOGGLE_COMPARE' }
  | { type: 'TOGGLE_METADATA' }
  | { type: 'TOGGLE_MINIMAP' }
  | { type: 'TOGGLE_DIAGRAM' }
  | { type: 'SET_SHOW_COMPARE'; payload: boolean }
  | { type: 'SET_SHOW_METADATA'; payload: boolean }
  | { type: 'SET_SHOW_MINIMAP'; payload: boolean }
  | { type: 'SET_SHOW_DIAGRAM'; payload: boolean }
  // Editor utilities
  | { type: 'SET_SCHEMAS_FOLDED'; payload: boolean }
  | { type: 'SET_SELECTED_SQL_QUERY'; payload: string | null }
  | { type: 'SET_SHOW_SCHEMA_BUTTON'; payload: boolean }
  // Environment & Deployment
  | { type: 'SET_SELECTED_ENVIRONMENT'; payload: string | null }
  | { type: 'SET_CURRENT_VERSION'; payload: number }
  | { type: 'SET_VERSIONS'; payload: TransformerVersion[] }
  | { type: 'SET_VERSION_IS_DEPLOYED'; payload: boolean }
  | { type: 'SET_IS_DEPLOYING'; payload: boolean }
  | { type: 'SET_IS_LATEST_VERSION'; payload: boolean }
  // Templates
  | { type: 'SET_SHOW_TEMPLATE_SELECTION'; payload: boolean }
  | { type: 'SET_SELECTED_TEMPLATE'; payload: string }
  | { type: 'SET_AVAILABLE_TEMPLATES'; payload: string[] }
  // Modals
  | { type: 'TOGGLE_VERSIONS_DIALOG' }
  | { type: 'TOGGLE_CHECKIN_DIALOG' }
  | { type: 'TOGGLE_DEPLOY_DIALOG' }
  | { type: 'TOGGLE_SCHEDULE_DIALOG' }
  | { type: 'TOGGLE_TEMPLATE_DIALOG' }
  | { type: 'TOGGLE_METADATA_DIALOG' }
  | { type: 'TOGGLE_SQL_EDITOR_DIALOG' }
  | { type: 'TOGGLE_SAVE_CONFLICT_DIALOG' }
  | { type: 'TOGGLE_UNSAVED_CHANGES_DIALOG' }
  | { type: 'TOGGLE_COMMAND_PALETTE' }
  | { type: 'SET_SHOW_VERSIONS_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_CHECKIN_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_DEPLOY_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_SCHEDULE_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_TEMPLATE_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_METADATA_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_SQL_EDITOR_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_SAVE_CONFLICT_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_UNSAVED_CHANGES_DIALOG'; payload: boolean }
  | { type: 'SET_SHOW_COMMAND_PALETTE'; payload: boolean }
  // Secondary toolbar
  | { type: 'TOGGLE_SECONDARY_TOOLBAR' }
  | { type: 'SET_SHOW_SECONDARY_TOOLBAR'; payload: boolean }
  // Flow save state
  | { type: 'SET_FLOW_SAVE_STATE'; payload: Partial<RecipeEditorState['flowSaveState']> }
  // Flow UI state
  | { type: 'SET_ERROR'; payload: boolean }
  | { type: 'SET_ERROR_MESSAGE'; payload: string | null }
  | { type: 'SET_IS_SIDEBAR_COLLAPSED'; payload: boolean }
  | { type: 'SET_REACT_FLOW_KEY'; payload: number }
  | { type: 'INCREMENT_REACT_FLOW_KEY' };

interface RecipeEditorContextValue {
  state: RecipeEditorState;
  dispatch: React.Dispatch<RecipeEditorAction>;
  // Convenience setters
  setRecipe: (recipe: Recipe | null) => void;
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  setYamlContent: (content: string) => void;
  setEditorMode: (mode: EditorMode) => void;
  setIsModified: (isModified: boolean) => void;
  setIsReadOnly: (isReadOnly: boolean) => void;
}

// ========================================
// Initial State
// ========================================

const initialState: RecipeEditorState = {
  // Recipe data
  recipe: null,
  nodes: [],
  edges: [],
  yamlContent: '',
  recipeMetadata: null,

  // Editor state
  editorMode: 'yaml',
  isModified: false,
  isReadOnly: false,
  isSaving: false,
  isSwitchingMode: false,
  isSwitchingRecipe: false,

  // History
  canUndo: false,
  canRedo: false,

  // File operations
  autosaveEnabled: false,
  highlightSave: false,

  // Execution
  jobMode: 'runAsJob',
  isExecuting: false,
  currentExternalExecutionId: null,

  // Bottom panel tabs
  bottomPanelTabs: [],
  activeBottomTabId: null,

  // Panels/Views
  showCompare: false,
  showMetadata: false,
  showMinimap: true,
  showDiagram: false,

  // Editor utilities
  schemasFolded: false,
  selectedSqlQuery: null,
  showSchemaButton: false,

  // Environment & Deployment
  selectedEnvironment: null,
  currentVersion: 1,
  versions: [],
  versionIsDeployed: false,
  isDeploying: false,
  isLatestVersion: true,

  // Templates
  showTemplateSelection: false,
  selectedTemplate: '',
  availableTemplates: [],

  // Modals
  showVersionsDialog: false,
  showCheckInDialog: false,
  showDeployDialog: false,
  showScheduleDialog: false,
  showTemplateDialog: false,
  showMetadataDialog: false,
  showSqlEditorDialog: false,
  showSaveConflictDialog: false,
  showUnsavedChangesDialog: false,
  showCommandPalette: false,

  // Secondary toolbar
  showSecondaryToolbar: true,

  // Flow save state
  flowSaveState: {
    isSaving: false,
    hasUnsavedChanges: false,
    isComparingRecipes: false,
  },

  // Flow UI state
  error: false,
  errorMessage: null,
  isSidebarCollapsed: false,
  reactFlowKey: 0,
};

// ========================================
// Reducer
// ========================================

function recipeEditorReducer(
  state: RecipeEditorState,
  action: RecipeEditorAction
): RecipeEditorState {
  switch (action.type) {
    // Recipe data
    case 'SET_RECIPE':
      return { ...state, recipe: action.payload };
    case 'SET_NODES':
      return { ...state, nodes: action.payload };
    case 'SET_EDGES':
      return { ...state, edges: action.payload };
    case 'SET_YAML_CONTENT':
      return { ...state, yamlContent: action.payload };
    case 'SET_RECIPE_METADATA':
      return { ...state, recipeMetadata: action.payload };

    // Editor state
    case 'SET_EDITOR_MODE':
      return { ...state, editorMode: action.payload, showDiagram: action.payload === 'visual' };
    case 'SET_IS_MODIFIED':
      return { ...state, isModified: action.payload, highlightSave: action.payload };
    case 'SET_IS_READ_ONLY':
      return { ...state, isReadOnly: action.payload };
    case 'SET_IS_SAVING':
      return { ...state, isSaving: action.payload };
    case 'SET_IS_SWITCHING_MODE':
      return { ...state, isSwitchingMode: action.payload };
    case 'SET_IS_SWITCHING_RECIPE':
      return { ...state, isSwitchingRecipe: action.payload };

    // History
    case 'SET_CAN_UNDO':
      return { ...state, canUndo: action.payload };
    case 'SET_CAN_REDO':
      return { ...state, canRedo: action.payload };

    // File operations
    case 'SET_AUTOSAVE_ENABLED':
      return { ...state, autosaveEnabled: action.payload };
    case 'SET_HIGHLIGHT_SAVE':
      return { ...state, highlightSave: action.payload };

    // Execution
    case 'SET_JOB_MODE':
      return { ...state, jobMode: action.payload };
    case 'SET_IS_EXECUTING':
      return { ...state, isExecuting: action.payload };
    case 'SET_CURRENT_EXTERNAL_EXECUTION_ID':
      return { ...state, currentExternalExecutionId: action.payload };

    // Bottom panel tabs
    case 'ADD_BOTTOM_TAB': {
      // Don't add duplicate tab types (except runningStatus can be re-added)
      const exists = state.bottomPanelTabs.find((t) => t.id === action.payload.id);
      if (exists) return { ...state, activeBottomTabId: action.payload.id };
      return {
        ...state,
        bottomPanelTabs: [...state.bottomPanelTabs, action.payload],
        activeBottomTabId: action.payload.id,
      };
    }
    case 'REMOVE_BOTTOM_TAB': {
      const filtered = state.bottomPanelTabs.filter((t) => t.id !== action.payload);
      let newActive = state.activeBottomTabId;
      if (state.activeBottomTabId === action.payload) {
        newActive = filtered.length > 0 ? filtered[filtered.length - 1].id : null;
      }
      return { ...state, bottomPanelTabs: filtered, activeBottomTabId: newActive };
    }
    case 'SET_ACTIVE_BOTTOM_TAB':
      return { ...state, activeBottomTabId: action.payload };
    case 'RENAME_BOTTOM_TAB':
      return {
        ...state,
        bottomPanelTabs: state.bottomPanelTabs.map((t) =>
          t.id === action.payload.id ? { ...t, title: action.payload.title } : t
        ),
      };
    case 'SET_BOTTOM_TAB_CLOSEABLE':
      return {
        ...state,
        bottomPanelTabs: state.bottomPanelTabs.map((t) =>
          t.id === action.payload.id ? { ...t, closeable: action.payload.closeable } : t
        ),
      };

    // Panels/Views
    case 'TOGGLE_COMPARE':
      return { ...state, showCompare: !state.showCompare };
    case 'TOGGLE_METADATA':
      return { ...state, showMetadata: !state.showMetadata };
    case 'TOGGLE_MINIMAP':
      return { ...state, showMinimap: !state.showMinimap };
    case 'TOGGLE_DIAGRAM':
      return {
        ...state,
        showDiagram: !state.showDiagram,
        editorMode: state.showDiagram ? 'yaml' : 'visual',
      };
    case 'SET_SHOW_COMPARE':
      return { ...state, showCompare: action.payload };
    case 'SET_SHOW_METADATA':
      return { ...state, showMetadata: action.payload };
    case 'SET_SHOW_MINIMAP':
      return { ...state, showMinimap: action.payload };
    case 'SET_SHOW_DIAGRAM':
      return {
        ...state,
        showDiagram: action.payload,
        editorMode: action.payload ? 'visual' : 'yaml',
      };

    // Editor utilities
    case 'SET_SCHEMAS_FOLDED':
      return { ...state, schemasFolded: action.payload };
    case 'SET_SELECTED_SQL_QUERY':
      return { ...state, selectedSqlQuery: action.payload };
    case 'SET_SHOW_SCHEMA_BUTTON':
      return { ...state, showSchemaButton: action.payload };

    // Environment & Deployment
    case 'SET_SELECTED_ENVIRONMENT':
      return { ...state, selectedEnvironment: action.payload };
    case 'SET_CURRENT_VERSION':
      return { ...state, currentVersion: action.payload };
    case 'SET_VERSIONS':
      return { ...state, versions: action.payload };
    case 'SET_VERSION_IS_DEPLOYED':
      return { ...state, versionIsDeployed: action.payload };
    case 'SET_IS_DEPLOYING':
      return { ...state, isDeploying: action.payload };
    case 'SET_IS_LATEST_VERSION':
      return { ...state, isLatestVersion: action.payload };

    // Templates
    case 'SET_SHOW_TEMPLATE_SELECTION':
      return { ...state, showTemplateSelection: action.payload };
    case 'SET_SELECTED_TEMPLATE':
      return { ...state, selectedTemplate: action.payload };
    case 'SET_AVAILABLE_TEMPLATES':
      return { ...state, availableTemplates: action.payload };

    // Modals
    case 'TOGGLE_VERSIONS_DIALOG':
      return { ...state, showVersionsDialog: !state.showVersionsDialog };
    case 'TOGGLE_CHECKIN_DIALOG':
      return { ...state, showCheckInDialog: !state.showCheckInDialog };
    case 'TOGGLE_DEPLOY_DIALOG':
      return { ...state, showDeployDialog: !state.showDeployDialog };
    case 'TOGGLE_SCHEDULE_DIALOG':
      return { ...state, showScheduleDialog: !state.showScheduleDialog };
    case 'TOGGLE_TEMPLATE_DIALOG':
      return { ...state, showTemplateDialog: !state.showTemplateDialog };
    case 'TOGGLE_METADATA_DIALOG':
      return { ...state, showMetadataDialog: !state.showMetadataDialog };
    case 'TOGGLE_SQL_EDITOR_DIALOG':
      return { ...state, showSqlEditorDialog: !state.showSqlEditorDialog };
    case 'TOGGLE_SAVE_CONFLICT_DIALOG':
      return { ...state, showSaveConflictDialog: !state.showSaveConflictDialog };
    case 'TOGGLE_UNSAVED_CHANGES_DIALOG':
      return { ...state, showUnsavedChangesDialog: !state.showUnsavedChangesDialog };
    case 'TOGGLE_COMMAND_PALETTE':
      return { ...state, showCommandPalette: !state.showCommandPalette };
    case 'SET_SHOW_VERSIONS_DIALOG':
      return { ...state, showVersionsDialog: action.payload };
    case 'SET_SHOW_CHECKIN_DIALOG':
      return { ...state, showCheckInDialog: action.payload };
    case 'SET_SHOW_DEPLOY_DIALOG':
      return { ...state, showDeployDialog: action.payload };
    case 'SET_SHOW_SCHEDULE_DIALOG':
      return { ...state, showScheduleDialog: action.payload };
    case 'SET_SHOW_TEMPLATE_DIALOG':
      return { ...state, showTemplateDialog: action.payload };
    case 'SET_SHOW_METADATA_DIALOG':
      return { ...state, showMetadataDialog: action.payload };
    case 'SET_SHOW_SQL_EDITOR_DIALOG':
      return { ...state, showSqlEditorDialog: action.payload };
    case 'SET_SHOW_SAVE_CONFLICT_DIALOG':
      return { ...state, showSaveConflictDialog: action.payload };
    case 'SET_SHOW_UNSAVED_CHANGES_DIALOG':
      return { ...state, showUnsavedChangesDialog: action.payload };
    case 'SET_SHOW_COMMAND_PALETTE':
      return { ...state, showCommandPalette: action.payload };

    // Secondary toolbar
    case 'TOGGLE_SECONDARY_TOOLBAR':
      return { ...state, showSecondaryToolbar: !state.showSecondaryToolbar };
    case 'SET_SHOW_SECONDARY_TOOLBAR':
      return { ...state, showSecondaryToolbar: action.payload };

    // Flow save state
    case 'SET_FLOW_SAVE_STATE':
      return { ...state, flowSaveState: { ...state.flowSaveState, ...action.payload } };

    // Flow UI state
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_ERROR_MESSAGE':
      return { ...state, errorMessage: action.payload };
    case 'SET_IS_SIDEBAR_COLLAPSED':
      return { ...state, isSidebarCollapsed: action.payload };
    case 'SET_REACT_FLOW_KEY':
      return { ...state, reactFlowKey: action.payload };
    case 'INCREMENT_REACT_FLOW_KEY':
      return { ...state, reactFlowKey: state.reactFlowKey + 1 };

    default:
      return state;
  }
}

// ========================================
// Context
// ========================================

const RecipeEditorContext = createContext<RecipeEditorContextValue | undefined>(undefined);

export const RecipeEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(recipeEditorReducer, initialState);

  // Convenience setters
  const setRecipe = useCallback(
    (recipe: Recipe | null) => dispatch({ type: 'SET_RECIPE', payload: recipe }),
    []
  );

  const setNodes = useCallback(
    (nodes: Node[]) => dispatch({ type: 'SET_NODES', payload: nodes }),
    []
  );

  const setEdges = useCallback(
    (edges: Edge[]) => dispatch({ type: 'SET_EDGES', payload: edges }),
    []
  );

  const setYamlContent = useCallback(
    (content: string) => dispatch({ type: 'SET_YAML_CONTENT', payload: content }),
    []
  );

  const setEditorMode = useCallback(
    (mode: EditorMode) => dispatch({ type: 'SET_EDITOR_MODE', payload: mode }),
    []
  );

  const setIsModified = useCallback(
    (isModified: boolean) => dispatch({ type: 'SET_IS_MODIFIED', payload: isModified }),
    []
  );

  const setIsReadOnly = useCallback(
    (isReadOnly: boolean) => dispatch({ type: 'SET_IS_READ_ONLY', payload: isReadOnly }),
    []
  );

  const value: RecipeEditorContextValue = {
    state,
    dispatch,
    setRecipe,
    setNodes,
    setEdges,
    setYamlContent,
    setEditorMode,
    setIsModified,
    setIsReadOnly,
  };

  return <RecipeEditorContext.Provider value={value}>{children}</RecipeEditorContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- Custom hook export is intentional
export const useRecipeEditor = (): RecipeEditorContextValue => {
  const context = useContext(RecipeEditorContext);
  if (!context) {
    throw new Error('useRecipeEditor must be used within a RecipeEditorProvider');
  }
  return context;
};
